import { Order } from "@redotech/redo-model/order";
import { ReturnOptionMethod } from "./return-flow";
import { Channel, SalesChannels } from "./sales-channels";

const CREDIT = ReturnOptionMethod.CREDIT;
const REFUND = ReturnOptionMethod.REFUND;
const EXCHANGE = ReturnOptionMethod.EXCHANGE;

export type ReturnTotals = {
  productTotals: ProductTotals[];
  totalStoreCredit: number;
  totalRefund: number;
  totalExchangeValue: number;
  totalReturnValue: number; // including taxes and adjustments
  returnValueTax: number; // just the taxes for items being returned
  totalRawReturnValue: number; // not including taxes or adjustments
  totalAdjustmentValue: number; // sum total of all upsells and fees (not including merchant adjustment)
  totalDiscount: number; // not including Redo discounts
  newOrderValue: number; // includes discounts and taxes
  newOrderDiscount: number;
  newOrderTaxes: number;
  totalTaxes: number; // from the original order
  // does not include taxes. Used for calculating the draft order price
  // (taxes will be added on to the draft order by shopify)
  charge: number;
  displayCharge: number; // takes into account taxes from new order and original order
  refundableDeposit: number;
  recoveryFee: number;
  shippingFee: number;
  shippingFeeDeducted: number;
};

export type ProductTotals = {
  product: Product;
  discountPrice: number; // includes discounts (if any), does not include tax or price adjustment
  originalPrice: number; // does not include discounts, tax, or price adjustment
  finalPrice: number; // includes discounts, tax, price adjustment, and merchant adjustment (if any)
  priceAdjustment: number;
  merchantAdjustment: number;
  tax: number;
  totalDiscount: number; // not including Redo discounts
};

type Product = {
  _id?: string;
  line_item_id: string;
  price: string;
  price_adjustment?: string | undefined;
  merchant_adjustment?: string;
  strategy: string;
  type: string;
  exchangeType: string;
};

type DiscountAllocation = {
  discountedAmount: {
    amount: string;
  };
};

type Return = {
  products: Product[];
  provision: string;
  stripe?: any;
  totals: {
    fee: number;
  };
  new_order_taxes?: string;
  discount_allocations?: DiscountAllocation[];
  advancedExchangeItems: { price: string }[];
  shippingFeeToDeduct?: number;
  draftOrderId?: number;
  labelDeductedFromCredit?: boolean;
};

type Team = {
  settings: {
    deductLabelFromCredit?: boolean;
    deductLabelFromRefund?: boolean;
  };
};

export class ReturnTotalsCalculator {
  private return_: Return;
  private productTotals: ProductTotals[];
  private order: Order;
  private team: Team;

  constructor(return_: Return, order: Order, team: Team) {
    this.return_ = return_;
    this.productTotals = [];
    this.order = order;

    this.team = team;
    this.calculateTotalsForProducts();
  }

  /**
   * Get taxes for a specific product in return
   */
  productTax({ line_item_id }: { line_item_id: String }): number {
    let taxValue = 0.0;
    const shopifyLineItem = this.order.shopify.line_items.find(
      (lineItem: any) => String(lineItem.id) === String(line_item_id),
    );
    const taxLines = shopifyLineItem?.tax_lines || [];
    for (const taxLine of taxLines) {
      taxValue += parseFloat(taxLine.price) / (shopifyLineItem?.quantity || 1);
    }

    return taxValue;
  }

  /**
   * Get taxes for entire return
   */
  returnTax(return_: Return): number {
    let taxValue = 0.0;
    const products = return_.products || [];
    for (const product of products) {
      taxValue += this.productTax(product);
    }
    return parseFloat(taxValue.toFixed(2));
  }

  private static getDiscountAllocations(lineItem: any, order: Order) {
    if (!lineItem.discount_allocations.length) {
      return 0;
    }

    let discountAmount = 0;
    lineItem.discount_allocations.forEach((discount: any) => {
      // Ignore redo discounts as we want the return value to include
      // discounts from previous returns (i.e. customers should be
      // able to return items that were previously returned)
      const discount_code =
        order.shopify.discount_applications[discount.discount_application_index]
          ?.code;
      if (!discount_code?.includes("REDO")) {
        discountAmount += +discount.amount;
      }
    });

    return discountAmount;
  }

  private static optional<T, V>(
    value: T | null | undefined,
    fn: (value: T) => V,
  ): V | null {
    if (value === null || value === undefined) {
      return null;
    }

    return fn(value);
  }

  static getOriginItemPrice(lineItem: any) {
    const originItemPrice = ReturnTotalsCalculator.optional(
      lineItem.properties?.find(
        ({ name }: { name: string }) => name === "_redo_origin_item",
      ),
      ({ value }) => {
        const payload: {
          unitPrice?: string;
        } = JSON.parse(value);

        return payload?.unitPrice || null;
      },
    );

    return originItemPrice;
  }

  static getDiscountPrice(lineItem: any, order: Order) {
    const originPrice = ReturnTotalsCalculator.getOriginItemPrice(lineItem);
    if (originPrice) {
      return originPrice;
    }

    const discountAmount = ReturnTotalsCalculator.getDiscountAllocations(
      lineItem,
      order,
    );

    return (lineItem.price - discountAmount / (lineItem.quantity || 1)).toFixed(
      2,
    );
  }

  /**
   * Calculate totals for each product, including discount price, price adjustments, and tax
   */
  private calculateTotalsForProducts() {
    for (const product of this.return_.products) {
      this.productTotals.push(this.calculateTotalsForProduct(product));
    }
  }

  /**
   * Calculate totals for a specific product, including discount price, price adjustments, and tax
   */
  calculateTotalsForProduct(product: Product) {
    const shopifyLineItem = this.order.shopify.line_items.find(
      (lineItem: any) => String(lineItem.id) === String(product.line_item_id),
    );

    const tax = this.productTax(product);
    const discountPrice = parseFloat(
      ReturnTotalsCalculator.getDiscountPrice(shopifyLineItem, this.order),
    );

    const originPrice =
      ReturnTotalsCalculator.getOriginItemPrice(shopifyLineItem);
    const originalPrice = originPrice
      ? parseFloat(originPrice)
      : parseFloat(shopifyLineItem?.price);

    return {
      product,
      discountPrice,
      originalPrice,
      priceAdjustment: parseFloat(product.price_adjustment || "0"),
      merchantAdjustment: parseFloat(product.merchant_adjustment || "0"),
      finalPrice:
        discountPrice +
        tax +
        parseFloat(product.price_adjustment || "0") +
        parseFloat(product.merchant_adjustment || "0"),
      tax: tax,
      totalDiscount:
        ReturnTotalsCalculator.getDiscountAllocations(
          shopifyLineItem,
          this.order,
        ) / (shopifyLineItem?.quantity || 1),
    };
  }

  /**
   * Calculate totals for all products of return
   */
  getTotalsForAllProducts() {
    return this.getTotalsForProducts(
      this.return_.products.map((product) => ({
        _id: product._id,
        line_item_id: product.line_item_id,
      })),
    );
  }

  /**
   * Calculate totals for specific products
   */
  getTotalsForProducts(products: { line_item_id?: string; _id?: string }[]) {
    // This accomodates calculating totals for products both before and after the return has been
    // created. If the return has been created, we really on the _id of the product. If not,
    // We rely on the line_item_id and quantities
    const productsToProcess: ProductTotals[] = [];
    this.productTotals.forEach((productTotals) => {
      // Ensure there aren't more productTotals with the same line item than are in lineItemIds
      const quantityRequested = products.filter(
        (product) =>
          product.line_item_id === productTotals.product.line_item_id,
      ).length;
      const quantityAdded = productsToProcess.filter(
        (product) =>
          product.product.line_item_id === productTotals.product.line_item_id,
      ).length;
      if (
        quantityAdded >= quantityRequested &&
        !products.some((product) => product._id !== null)
      ) {
        return;
      }
      if (
        products.some(
          (product) =>
            product._id === productTotals.product._id ||
            (!product._id &&
              product.line_item_id === productTotals.product.line_item_id),
        )
      ) {
        productsToProcess.push(productTotals);
      }
    });

    let storeCreditTotals = this.getStoreCreditAmount(productsToProcess);
    let refundTotals = this.getRefundAmount(productsToProcess);
    let chargeTotals = this.getExchangeFee(productsToProcess);

    if (chargeTotals.amount < 0) {
      // The new order value is less than the return value,
      // so we need to refund the difference in store credit
      storeCreditTotals.amount += (chargeTotals.amount + chargeTotals.tax) * -1;
      chargeTotals.amount = 0;
    }

    // If there is a charge, apply any existing store credit
    if (chargeTotals.amount) {
      chargeTotals.amount -= storeCreditTotals.amount;
      storeCreditTotals.amount -=
        chargeTotals.amount + storeCreditTotals.amount;

      chargeTotals.tax -= storeCreditTotals.tax;
      storeCreditTotals.tax -= chargeTotals.tax + storeCreditTotals.tax;

      if (storeCreditTotals.amount < 0) {
        storeCreditTotals.amount = 0;
        storeCreditTotals.tax = 0;
      }
      if (chargeTotals.amount < 0) {
        chargeTotals.amount = 0;
        chargeTotals.tax = 0;
      }
    }

    // If there is still a charge, apply any existing refund
    if (chargeTotals.amount) {
      chargeTotals.amount -= refundTotals.amount;
      refundTotals.amount -= chargeTotals.amount + refundTotals.amount;
      refundTotals.tax -= chargeTotals.tax;
      if (refundTotals.amount < 0) {
        refundTotals.amount = 0;
      }
      if (chargeTotals.amount < 0) {
        chargeTotals.amount = 0;
      }
    }

    const returnValueTotals = this.getTotalReturnValue(productsToProcess);
    const rawReturnValueTotals = this.getRawReturnValue(productsToProcess);
    const newOrderValueTotals = this.getNewOrderValue(productsToProcess);
    const totalAdjustmentValue = this.getTotalAdjustmentValue();
    const exchangeValueTotals = this.getTotalExchangeValue(productsToProcess);
    const totalTaxes = this.returnTax(this.return_);

    const totalReturnValue = returnValueTotals.amount + returnValueTotals.tax;
    const totalExchangeValue =
      exchangeValueTotals.amount + exchangeValueTotals.tax;
    let totalStoreCredit = storeCreditTotals.amount
      ? storeCreditTotals.amount + storeCreditTotals.tax
      : 0;
    let totalRefund = refundTotals.amount
      ? refundTotals.amount + refundTotals.tax
      : 0;
    const newOrderValue = newOrderValueTotals.amount + newOrderValueTotals.tax;
    let displayCharge = newOrderValue - totalReturnValue;
    if (displayCharge < 0) {
      displayCharge = 0;
    }

    let shippingFee =
      this.return_.shippingFeeToDeduct || this.return_.totals.fee;
    const salesChannelIdsToExclude = ["facebook"];
    const channelsToExclude = SalesChannels.filter((channel: Channel) =>
      salesChannelIdsToExclude.includes(channel.id),
    );
    let exclude = false;
    channelsToExclude.forEach((channel: Channel) => {
      channel.sourceNames.forEach((sourceName: string) => {
        if (this.order.shopify.source_name.startsWith(sourceName)) {
          exclude = true;
        }
      });
    });
    // If a product has an _id, it means we are not on the return portal
    // If we are not on the return portal (e.g. we're on the merchant dashboard), we should only deduct
    // the shipping fee if it was deducted in the return portal (i.e. labelDeductedFromCredit = true)
    const isReturnPortal = !this.return_.products[0]._id;
    if (!exclude && (this.return_.labelDeductedFromCredit || isReturnPortal)) {
      if (
        totalRefund > 0 &&
        shippingFee > 0 &&
        this.team.settings.deductLabelFromRefund
      ) {
        if (totalRefund >= shippingFee) {
          // Deduct the shipping cost from refund value, but only when it can cover the whole cost
          totalRefund = totalRefund - shippingFee;
          shippingFee = 0;
        } else if (this.return_.shippingFeeToDeduct) {
          // The shipping cost needs to be reducted from the refund value, but the return is being
          // partially processed, so deduct some now and the rest later
          shippingFee -= totalRefund;
          totalRefund = 0;
        }
      }

      if (
        this.team.settings.deductLabelFromCredit &&
        totalStoreCredit > 0 &&
        shippingFee > 0
      ) {
        if (totalStoreCredit > shippingFee) {
          // Deduct the shipping cost from store credit, but only when it can cover the whole cost
          totalStoreCredit = totalStoreCredit - shippingFee;
          shippingFee = 0;
        } else if (this.return_.shippingFeeToDeduct) {
          // The shipping cost needs to be reducted from the store credit value, but the return is being
          // partially processed, so deduct some now and the rest later
          shippingFee -= totalStoreCredit;
          totalStoreCredit = 0;
        }
      }
    }

    let refundableDeposit = 0;
    let recoveryFee = 0;
    if (this.return_.provision === "instant") {
      refundableDeposit =
        totalReturnValue > newOrderValue ? newOrderValue : totalReturnValue;
      if (this.return_.stripe) {
        recoveryFee = this.getRecoveryFee(
          productsToProcess,
          displayCharge,
          totalReturnValue,
          newOrderValue,
        );
      }
      // Rare case where there is an instant exchange and the new order value is more than the return value
      // If there are positive merchant adjustments, we need to add them to the store credit/refund
      // amounts instead of deducting them from the charge, since the draft order was already created
      // and it is no longer possible to change the price
      if (this.return_.draftOrderId) {
        chargeTotals.amount = 0;
        displayCharge = 0;

        if (
          newOrderValue >
          rawReturnValueTotals.amount + rawReturnValueTotals.tax
        ) {
          const storeCreditMerchantAdjustment = this.productTotals
            .filter(
              (product) =>
                product.product.type === "return" &&
                product.product.strategy === "store_credit",
            )
            .reduce((total, product) => {
              return total + product.merchantAdjustment;
            }, 0);

          const refundMerchantAdjustment = this.productTotals
            .filter((product) => product.product.strategy === "refund")
            .reduce((total, product) => {
              return total + product.merchantAdjustment;
            }, 0);

          const exchangeMerchantAdjustment = this.productTotals
            .filter((product) => product.product.type === "exchange")
            .reduce((total, product) => {
              return total + product.merchantAdjustment;
            }, 0);

          totalStoreCredit =
            storeCreditMerchantAdjustment + exchangeMerchantAdjustment;
          if (totalStoreCredit < 0) {
            totalStoreCredit = 0;
          }

          totalRefund = refundMerchantAdjustment;
          if (totalRefund < 0) {
            totalRefund = 0;
          }
        }
      }
    }

    let newOrderDiscount = 0;
    let newOrderTaxes = 0;
    if (this.return_.new_order_taxes && this.return_.discount_allocations) {
      newOrderDiscount = this.getTotalNewOrderDiscountAllocations();
      newOrderTaxes = parseFloat(this.return_.new_order_taxes);
    }

    const totalDiscount = this.getTotalDiscount(productsToProcess);

    const totals: ReturnTotals = {
      productTotals: this.productTotals,
      totalStoreCredit,
      totalRefund,
      totalExchangeValue,
      totalReturnValue,
      returnValueTax: returnValueTotals.tax,
      totalRawReturnValue: rawReturnValueTotals.amount,
      totalAdjustmentValue,
      totalDiscount,
      newOrderValue,
      newOrderDiscount,
      newOrderTaxes,
      totalTaxes,
      charge: chargeTotals.amount,
      displayCharge,
      refundableDeposit: refundableDeposit,
      recoveryFee: recoveryFee,
      shippingFee: shippingFee,
      shippingFeeDeducted: this.return_.totals.fee - shippingFee,
    };

    return totals;
  }

  /**
   * Get the total price of all items being returned for a specific return type (credit, refund, or exchange)
   *
   * Note: the merchant adjustment (and price adjustment) is split between the product price and the product tax.
   *  Example: productPrice = $10, tax = $0.72, merchantAdjustment = -$5
   *  merchantAdjustmentPercentage = ($10 + $0.72 - $5) / ($10 + $0.72) = 0.534
   *  productPrice = $10 * 0.534 = $5.34
   *  tax = $0.72 * 0.534 = $0.38
   *  total = $5.34 + $0.38 = $5.72 (which is the same as $10 + $0.72 - $5)
   *
   * This is important when applying exchange discounts to draft orders, we apply it without taxes since
   *  taxes are calculated after discounts.
   */
  private getTotalValueForType(
    returnType: typeof CREDIT | typeof REFUND | typeof EXCHANGE,
    productsToProcess: ProductTotals[],
    includeAdjustments = true,
  ) {
    const type = returnType === EXCHANGE ? "exchange" : "return";
    const strategy = returnType === REFUND ? "refund" : "store_credit";

    let amount = 0;
    let tax = 0;
    for (const productTotals of productsToProcess) {
      if (
        productTotals.product.strategy === strategy &&
        productTotals.product.type === type
      ) {
        if (includeAdjustments) {
          const adjustments =
            productTotals.priceAdjustment + productTotals.merchantAdjustment;
          const productPrice = productTotals.discountPrice + productTotals.tax;
          if (productPrice === 0) {
            amount += adjustments;
          } else {
            const adjustmentPercentage =
              (productPrice + adjustments) / productPrice;
            amount += productTotals.discountPrice * adjustmentPercentage;
            tax += productTotals.tax * adjustmentPercentage;
          }
        } else {
          amount += productTotals.discountPrice;
          tax += productTotals.tax;
        }
      }
    }

    return { amount, tax };
  }

  getTotalAdjustmentValue() {
    return this.productTotals.reduce((total, product) => {
      return total + product.priceAdjustment;
    }, 0);
  }

  getTotalDiscount(productTotals: ProductTotals[]) {
    return productTotals.reduce((total, product) => {
      return total + product.totalDiscount;
    }, 0);
  }

  /**
   * Total price of all items being returned for store credit
   */
  getStoreCreditAmount(
    productsToProcess: ProductTotals[],
    includeAdjustments = true,
  ) {
    return this.getTotalValueForType(
      CREDIT,
      productsToProcess,
      includeAdjustments,
    );
  }

  /**
   * Total price of all items being returned for a refund
   */
  getRefundAmount(
    productsToProcess: ProductTotals[],
    includeAdjustments = true,
  ) {
    return this.getTotalValueForType(
      REFUND,
      productsToProcess,
      includeAdjustments,
    );
  }

  /**
   * Total price of all items being returned for an exchange
   */
  getTotalExchangeValue(
    productsToProcess: ProductTotals[],
    includeAdjustments = true,
  ) {
    return this.getTotalValueForType(
      EXCHANGE,
      productsToProcess,
      includeAdjustments,
    );
  }

  /**
   * Total price of all items being returned for an advanced exchange
   */
  getAdvancedExchangeValue(productsToProcess: ProductTotals[]) {
    return this.getTotalValueForType(
      EXCHANGE,
      productsToProcess.filter(
        (productTotals) => productTotals.product.exchangeType === "advanced",
      ),
    );
  }

  /**
   * Total price of all items being returned for a variant exchange
   */
  getVariantExchangeValue(
    productsToProcess: ProductTotals[],
    includeAdjustments = true,
  ) {
    return this.getTotalValueForType(
      EXCHANGE,
      productsToProcess.filter(
        (productTotals) => productTotals.product.exchangeType === "variant",
      ),
      includeAdjustments,
    );
  }

  /**
   * Total price of all new exchange items as part of the advanced exchange
   */
  getAdvancedExchangeItemsFee() {
    let exchangeFee = 0;
    for (const advancedExchangeItem of this.return_.advancedExchangeItems) {
      exchangeFee += parseFloat(advancedExchangeItem.price);
    }
    exchangeFee -= this.getTotalNewOrderDiscountAllocations();

    return {
      amount: exchangeFee,
      tax: parseFloat(this.return_.new_order_taxes || "0"),
    };
  }

  /**
   * Total price of all items being returned
   */
  getTotalReturnValue(productsToProcess: ProductTotals[]) {
    const storeCredit = this.getStoreCreditAmount(productsToProcess);
    const refund = this.getRefundAmount(productsToProcess);
    const exchangeValue = this.getTotalExchangeValue(productsToProcess);
    return {
      tax: storeCredit.tax + refund.tax + exchangeValue.tax,
      amount: storeCredit.amount + refund.amount + exchangeValue.amount,
    };
  }

  /**
   * Total price of all items being returned not including adjustments
   */
  getRawReturnValue(productsToProcess: ProductTotals[]) {
    const storeCredit = this.getStoreCreditAmount(productsToProcess, false);
    const refund = this.getRefundAmount(productsToProcess, false);
    const exchangeValue = this.getTotalExchangeValue(productsToProcess, false);
    return {
      tax: storeCredit.tax + refund.tax + exchangeValue.tax,
      amount: storeCredit.amount + refund.amount + exchangeValue.amount,
    };
  }

  /**
   * Total price of all items in the new order (including variant and advanced exchange items)
   */
  getNewOrderValue(productsToProcess: ProductTotals[]) {
    const variantExchangeValue = this.getVariantExchangeValue(
      productsToProcess,
      false,
    );
    const advancedExchangeItemsFee = this.getAdvancedExchangeItemsFee();

    return {
      tax: variantExchangeValue.tax + advancedExchangeItemsFee.tax,
      amount: variantExchangeValue.amount + advancedExchangeItemsFee.amount,
    };
  }

  /**
   * Net price of exchange items
   */
  getExchangeFee(productsToProcess: ProductTotals[]) {
    const advancedExchangeValue =
      this.getAdvancedExchangeValue(productsToProcess);
    const advancedExchangeFee = this.getAdvancedExchangeItemsFee();
    // There can be fees for variant exchange items when there are price/merchant adjustments
    const variantExchangeValue =
      this.getVariantExchangeValue(productsToProcess);
    const variantExchangeFee = this.getVariantExchangeValue(
      productsToProcess,
      false,
    );

    return {
      tax:
        advancedExchangeFee.tax +
        variantExchangeFee.tax -
        (advancedExchangeValue.tax + variantExchangeValue.tax),
      amount:
        advancedExchangeFee.amount +
        variantExchangeFee.amount -
        (advancedExchangeValue.amount + variantExchangeValue.amount),
    };
  }

  getRecoveryFee(
    productsToProcess: ProductTotals[],
    charge: number,
    returnValue: number,
    newOrderValue: number,
  ) {
    const totalMerchantAdjustments = productsToProcess.reduce(
      (total, product) => total + product.merchantAdjustment,
      0,
    );
    if (returnValue - totalMerchantAdjustments > newOrderValue) {
      return charge;
    }

    return totalMerchantAdjustments > 0 ? 0 : totalMerchantAdjustments * -1;
  }

  getTotalNewOrderDiscountAllocations() {
    if (!this.return_.discount_allocations) {
      return 0;
    }

    return this.return_.discount_allocations.reduce(
      (total: number, discount: DiscountAllocation) =>
        total + parseFloat(discount.discountedAmount.amount),
      0,
    );
  }
}
